import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ToggleButtonGroup,
  ToggleButton,
  Chip
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Helmet } from 'react-helmet-async';

interface PlanFeature {
  label: string;
  included: boolean;
}

interface Plan {
  name: string;
  price: { monthly: number; yearly: number };
  description: string;
  features: PlanFeature[];
  highlight?: boolean;
  cta: string;
}

const plans: Plan[] = [
  {
    name: 'FREE',
    price: { monthly: 0, yearly: 0 },
    description: 'ゲーム管理に必要な全て',
    cta: '現在のプラン',
    features: [
      { label: 'ゲームライブラリ', included: true },
      { label: 'レビュー投稿', included: true },
      { label: 'リスト作成', included: true },
      { label: 'GOTY/MyGOTY作成', included: true },
      { label: 'プレイログ', included: true },
      { label: 'Steamインポート', included: true },
    ],
  },
  {
    name: 'STANDARD',
    price: { monthly: 390, yearly: 3900 },
    description: 'もっと便利に',
    highlight: true,
    cta: 'LITE に加入',
    features: [
      { label: '広告非表示', included: true },
      { label: '非公開コンテンツI', included: true },
      { label: 'カスタムプロフィールI', included: true },
      { label: 'PROバッジ', included: true },
      { label: 'アクティビティ拡張I', included: true },
      { label: '2,000字の非公開メモ', included: true },
      { label: '500字のポスト', included: true },
      { label: '24時間のポスト編集', included: true },

    ],
  },
  {
    name: 'MAX',
    price: { monthly: 980, yearly: 9800 },
    description: 'すべての機能を解放',
    cta: 'MAX に加入',
    features: [
      { label: 'アーリーアクセス', included: true },
      { label: 'カスタムプロフィールII', included: true },
      { label: '非公開コンテンツII', included: true },
      { label: '上位PROバッジ', included: true },
      { label: 'アクティビティ拡張II', included: true },
      { label: 'ゲーム単位の非公開', included: true },
      { label: '20,000字の非公開メモ', included: true },
      { label: '2,000字のポスト', included: true },
      { label: '3ヶ月のポスト編集', included: true },
    ],
  },
];

const PricingPage: React.FC = () => {
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly');

  return (
    <>
      <Helmet>
        <title>料金プラン - PULSE PRO</title>
      </Helmet>
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'primary.main' }}>
            PULSE PRO
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
            あなたに合ったプランを選びましょう
          </Typography>
          <ToggleButtonGroup
            value={billing}
            exclusive
            onChange={(_, v) => v && setBilling(v)}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                px: { xs: 3, sm: 6 },
                py: 0.75,
                textTransform: 'none',
                fontSize: '0.85rem',
                border: '1px solid rgba(255,255,255,0.12)',
                '&.Mui-selected': {
                  bgcolor: 'transparent',
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  '&:hover': { bgcolor: 'rgba(217,255,0,0.08)' },
                },
              },
            }}
          >
            <ToggleButton value="monthly">月額</ToggleButton>
            <ToggleButton value="yearly">
              年額
              <Chip
                label="2ヶ月分お得"
                size="small"
                sx={{
                  ml: 0.75,
                  height: 20,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  bgcolor: 'rgba(217,255,0,0.15)',
                  color: 'primary.main',
                }}
              />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Plan Cards */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 3,
            alignItems: 'stretch',
            pt: 3,
          }}
        >
          {plans.map((plan) => {
            const isYearly = billing === 'yearly';
            const price = isYearly ? plan.price.yearly : plan.price.monthly;
            const monthlyEquivalent = isYearly && plan.price.yearly > 0
              ? Math.round(plan.price.yearly / 12)
              : null;
            const isHighlight = plan.highlight;

            return (
              <Card
                key={plan.name}
                sx={{
                  position: 'relative',
                  border: isHighlight
                    ? '2px solid'
                    : '1px solid rgba(255,255,255,0.08)',
                  borderColor: isHighlight ? 'primary.main' : undefined,
                  borderRadius: 3,
                  bgcolor: isHighlight
                    ? 'rgba(217,255,0,0.03)'
                    : 'background.paper',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'visible',
                  ":hover": {
                    transform: 'none',
                  },
                }}
              >
                {isHighlight && (
                  <Chip
                    label="おすすめ"
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -14,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: 'primary.main',
                      color: '#000',
                      fontWeight: 700,
                      fontSize: '0.7rem',
                      zIndex: 1,
                    }}
                  />
                )}
                <CardContent
                  sx={{
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    flexGrow: 1,
                  }}
                >
                  {/* Plan Name */}
                  <Typography
                    variant="overline"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: 2,
                      color: isHighlight ? 'primary.main' : 'text.secondary',
                      mb: 0.5,
                    }}
                  >
                    {plan.name}
                  </Typography>

                  {/* Description */}
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', mb: 2 }}
                  >
                    {plan.description}
                  </Typography>

                  {/* Price - fixed height so buttons align */}
                  <Box sx={{ mb: 3, minHeight: 80 }}>
                    {isYearly && plan.price.monthly > 0 && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.disabled',
                          textDecoration: 'line-through',
                          mb: 0.25,
                        }}
                      >
                        ¥{(plan.price.monthly * 12).toLocaleString()}/年
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        ¥{price.toLocaleString()}
                      </Typography>
                      {price > 0 && (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          /{isYearly ? '年' : '月'}
                        </Typography>
                      )}
                      {isYearly && plan.price.monthly > 0 && (
                        <Chip
                          label="16.6% OFF"
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            bgcolor: 'rgba(217,255,0,0.15)',
                            color: 'primary.main',
                          }}
                        />
                      )}
                    </Box>
                    {monthlyEquivalent && (
                      <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
                        月あたり ¥{monthlyEquivalent.toLocaleString()}
                      </Typography>
                    )}
                    {price === 0 && (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        ずっと無料
                      </Typography>
                    )}
                  </Box>

                  {/* CTA Button */}
                  <Button
                    variant={isHighlight ? 'contained' : 'outlined'}
                    fullWidth
                    disabled={plan.price.monthly === 0}
                    sx={{
                      mb: 3,
                      py: 1,
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 2,
                      ...(isHighlight
                        ? { bgcolor: 'primary.main', color: '#000' }
                        : {
                            borderColor: 'rgba(255,255,255,0.2)',
                            color: 'text.primary',
                          }),
                    }}
                  >
                    {plan.cta}
                  </Button>

                  {/* Features */}
                  <List dense disablePadding sx={{ flexGrow: 1 }}>
                    {plan.features.map((feature) => (
                      <ListItem key={feature.label} disableGutters sx={{ py: 0.4 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          {feature.included ? (
                            <CheckIcon
                              sx={{ fontSize: '1rem', color: 'primary.main' }}
                            />
                          ) : (
                            <CloseIcon
                              sx={{ fontSize: '1rem', color: 'text.disabled' }}
                            />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={feature.label}
                          primaryTypographyProps={{
                            variant: 'body2',
                            sx: {
                              color: feature.included
                                ? 'text.primary'
                                : 'text.disabled',
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      </Container>
    </>
  );
};

export default PricingPage;
